import React from 'react'
import { useIntl } from 'react-intl'

import styled from 'styled-components/native'

import {
  computeRemainderSessions,
  computeRemainderUsed,
  createRemainingSessionCountByProgram,
  isKnownProgramCategory,
  isProgramAvailableForMinor,
  isUnlimitedSessionLimit,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/sessionLimit'
import { formatDualSessionsText } from '@lyrahealth-inc/shared-app-logic/src/features/sessions/utils/dualSessionsFormatter'
import {
  ProgramCategory,
  SessionCountByProgram,
  SessionLimitByProgram,
} from '@lyrahealth-inc/shared-app-logic/src/models/session/SessionUsage'
import { BodyText } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { colors } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/colors'
import { BodyTextSize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'
import { ColumnView } from '@lyrahealth-inc/ui-core-crossplatform/src/templates/content/CommonViews'

import { programString, ProgramStringKey } from '../../constants/appConstants'

type ClientSessionLimitInfoProps = {
  program?: ProgramStringKey
  displayVisitsPerIssuePerYear?: number
  thirdPartySessionCount?: number
  companyName?: string
  displayVisitsPerIssuePerYearByProgram?: SessionCountByProgram
  usedSessionsByProgram?: SessionCountByProgram
  EAPSessionLimitByProgram?: SessionLimitByProgram
  sessionLimit?: number
  sessionUsed?: number
  benefitResetDate?: string
  isMinor?: boolean
  hideProgram?: ProgramCategory
  warningThreshold?: number
  bold?: boolean
  textColor?: string
  testID?: string
  eapLabel?: string
  showSessionsUsedPerIssue?: boolean
}

const ProgramLevelSessionLimitTextContainer = styled(ColumnView)({
  marginRight: '8px',
})

const DisclaimerText = styled(BodyText)({
  fontStyle: 'italic',
})

export const ClientSessionLimitInfo: React.FC<ClientSessionLimitInfoProps> = ({
  program = 'eap',
  sessionUsed,
  sessionLimit,
  displayVisitsPerIssuePerYear,
  displayVisitsPerIssuePerYearByProgram,
  usedSessionsByProgram,
  EAPSessionLimitByProgram,
  benefitResetDate,
  thirdPartySessionCount,
  companyName,
  isMinor,
  bold = false,
  textColor,
  hideProgram,
  warningThreshold,
  testID = 'ClientSessionLimitInfo',
  eapLabel,
  showSessionsUsedPerIssue = false,
}) => {
  const { formatDate } = useIntl()
  const eapDisplayLabel = eapLabel ?? programString.eap
  let sessionCountNode: React.ReactElement | null = null
  let formattedResetDate: string | undefined = undefined
  if (benefitResetDate) {
    formattedResetDate = formatDate(benefitResetDate, { month: 'long', day: 'numeric' })
  }
  if (displayVisitsPerIssuePerYear) {
    sessionCountNode = (
      <BodyText
        text={`${displayVisitsPerIssuePerYear} visits per issue per year. Issues determined between you and your client.`}
        bold={bold}
        testID={testID}
        color={textColor}
      />
    )
  } else if (displayVisitsPerIssuePerYearByProgram) {
    const dualSessionsText = formatDualSessionsText(displayVisitsPerIssuePerYearByProgram, EAPSessionLimitByProgram)

    if (dualSessionsText) {
      sessionCountNode = <BodyText text={dualSessionsText} bold={bold} testID={testID} color={textColor} />
    }
  } else if (EAPSessionLimitByProgram && program === programString.eap.toLowerCase()) {
    const remainingSessionsByProgram = createRemainingSessionCountByProgram(
      EAPSessionLimitByProgram,
      usedSessionsByProgram,
    )
    const remainder = computeRemainderSessions(sessionLimit, EAPSessionLimitByProgram)
    const programRows = Object.keys(remainingSessionsByProgram)
      .filter((key): key is ProgramCategory => isKnownProgramCategory(key))
      .map((programCategory, index) => {
        if (isMinor && !isProgramAvailableForMinor(programCategory)) return null
        if (hideProgram && hideProgram === programCategory) return null
        const programLimit = EAPSessionLimitByProgram[programCategory] ?? 0
        let sessionUsedText = ''
        let rowTextColor = textColor
        if (isUnlimitedSessionLimit(programLimit)) {
          sessionUsedText = `Unlimited ${programCategory} sessions.`
        } else {
          sessionUsedText = `${usedSessionsByProgram?.[programCategory] ?? 0} of ${programLimit} `
          if (formattedResetDate) {
            sessionUsedText += `${programCategory} sessions used. (${
              remainingSessionsByProgram?.[programCategory] ?? 0
            } remaining, benefit resets on ${formattedResetDate})`
          } else {
            sessionUsedText += `${eapDisplayLabel} ${programCategory} sessions used.`
          }
          if (
            warningThreshold &&
            remainingSessionsByProgram &&
            Object.prototype.hasOwnProperty.call(remainingSessionsByProgram, programCategory) &&
            remainingSessionsByProgram[programCategory]! < warningThreshold
          ) {
            rowTextColor = colors.red4
          }
        }
        return <BodyText key={index} text={sessionUsedText} bold={bold} color={rowTextColor} />
      })
    let remainderRow: React.ReactElement | null = null
    if (remainder > 0) {
      const remainderUsed = computeRemainderUsed(sessionUsed, EAPSessionLimitByProgram, usedSessionsByProgram)
      const remainderRemaining = Math.max(remainder - remainderUsed, 0)
      let remainderText = `${remainderUsed} of ${remainder} `
      if (formattedResetDate) {
        remainderText += `${eapDisplayLabel} sessions used. (${remainderRemaining} remaining, benefit resets on ${formattedResetDate})`
      } else {
        remainderText += `${eapDisplayLabel} sessions used.`
      }
      let remainderTextColor = textColor
      if (warningThreshold && remainderRemaining < warningThreshold) {
        remainderTextColor = colors.red4
      }
      remainderRow = <BodyText key='remainder' text={remainderText} bold={bold} color={remainderTextColor} />
    }
    sessionCountNode = (
      <ProgramLevelSessionLimitTextContainer testID={testID}>
        {programRows}
        {remainderRow}
      </ProgramLevelSessionLimitTextContainer>
    )
  } else if (sessionLimit !== undefined && sessionUsed !== undefined) {
    const sessionRemaining = sessionLimit - sessionUsed
    const sessionsUsedLabel = `sessions${showSessionsUsedPerIssue ? ' per issue' : ''} used.`
    let sessionUsedText = `${sessionUsed} of ${sessionLimit} `
    if (formattedResetDate) {
      sessionUsedText += `${sessionsUsedLabel} (${sessionRemaining} remaining, benefit resets on ${formattedResetDate})`
    } else if (program === programString.eap.toLowerCase()) {
      sessionUsedText += `${eapDisplayLabel} ${sessionsUsedLabel}`
    } else {
      sessionUsedText += sessionsUsedLabel
    }
    const useWarningColor = warningThreshold && sessionRemaining < warningThreshold
    if (thirdPartySessionCount != undefined) {
      const lyraSessionCount = sessionUsed - thirdPartySessionCount
      sessionUsedText += `\n\tLyra: ${lyraSessionCount} · Crossover: ${thirdPartySessionCount}`
      const limitLabel = companyName ? `${companyName} limit` : 'session limit'
      sessionCountNode = (
        <ColumnView testID={testID}>
          <BodyText text={sessionUsedText} bold={bold} color={useWarningColor ? colors.red4 : textColor} />
          <DisclaimerText
            text={`\t Crossover sessions count toward the ${limitLabel} but are billed separately and not visible in Provider Portal.`}
            color={textColor}
            size={BodyTextSize.SMALL}
          />
        </ColumnView>
      )
    } else {
      sessionCountNode = (
        <BodyText
          text={sessionUsedText}
          bold={bold}
          testID={testID}
          color={useWarningColor ? colors.red4 : textColor}
        />
      )
    }
  }

  return sessionCountNode
}
